import styled, { keyframes } from 'styled-components';
import { 
  galleryColors, 
  galleryShadows, 
  gallerySpacing,
  galleryBorderRadius
} from '../../styles/galleryHelpers';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const SpinnerContainer = styled.div<{ $size: string; $center?: boolean }>`
  display: ${({ $center }) => $center ? 'flex' : 'inline-flex'};
  align-items: center;
  justify-content: center;
  ${({ $center }) => $center && `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}
`;

const SpinnerWheel = styled.div<{ $size: string; $color: string }>`
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border: 3px solid ${({ theme }) => theme.colors.gray.light};
  border-top: 3px solid ${({ $color, theme }) => $color === 'primary' ? theme.colors.primary : 
    $color === 'secondary' ? theme.colors.secondary :
    $color === 'accent' ? theme.colors.gallery.accent.primary : $color};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const DotsContainer = styled.div<{ $size: string }>`
  display: flex;
  gap: ${({ $size }) => $size === 'sm' ? '4px' : $size === 'lg' ? '8px' : '6px'};
`;

const Dot = styled.div<{ $size: string; $color: string; $delay: number }>`
  width: ${({ $size }) => $size === 'sm' ? '6px' : $size === 'lg' ? '12px' : '8px'};
  height: ${({ $size }) => $size === 'sm' ? '6px' : $size === 'lg' ? '12px' : '8px'};
  background-color: ${({ $color, theme }) => $color === 'primary' ? theme.colors.primary : 
    $color === 'secondary' ? theme.colors.secondary :
    $color === 'accent' ? theme.colors.gallery.accent.primary : $color};
  border-radius: 50%;
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

const SpinnerText = styled.div<{ $size: string }>`
  margin-top: ${({ $size }) => $size === 'sm' ? '8px' : $size === 'lg' ? '16px' : '12px'};
  font-size: ${({ theme, $size }) => 
    $size === 'sm' ? theme.typography.fontSize.sm : 
    $size === 'lg' ? theme.typography.fontSize.lg : 
    theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.gray.dark};
  text-align: center;
`;

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'accent' | string;
  type?: 'wheel' | 'dots';
  text?: string;
  center?: boolean;
  className?: string;
}

export function LoadingSpinner({
  size = 'md',
  color = 'primary',
  type = 'wheel',
  text,
  center = false,
  className,
}: LoadingSpinnerProps) {
  const sizeMap = {
    sm: '20px',
    md: '32px',
    lg: '48px',
  };

  const spinnerSize = sizeMap[size];

  return (
    <SpinnerContainer $size={spinnerSize} $center={center} className={className}>
      <div>
        {type === 'wheel' ? (
          <SpinnerWheel $size={spinnerSize} $color={color} />
        ) : (
          <DotsContainer $size={size}>
            <Dot $size={size} $color={color} $delay={0} />
            <Dot $size={size} $color={color} $delay={0.2} />
            <Dot $size={size} $color={color} $delay={0.4} />
          </DotsContainer>
        )}
        {text && <SpinnerText $size={size}>{text}</SpinnerText>}
      </div>
    </SpinnerContainer>
  );
}

// Overlay spinner for full-page loading
const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface LoadingOverlayProps extends Omit<LoadingSpinnerProps, 'center'> {
  visible: boolean;
}

export function LoadingOverlay({ visible, ...spinnerProps }: LoadingOverlayProps) {
  if (!visible) return null;

  return (
    <OverlayContainer>
      <LoadingSpinner {...spinnerProps} />
    </OverlayContainer>
  );
}

// Elegant shimmer animation for gallery mode
const galleryShimmer = keyframes`
  0% { 
    background-position: -100% 0; 
    opacity: 0.3;
  }
  50% { 
    opacity: 0.7;
  }
  100% { 
    background-position: 100% 0; 
    opacity: 0.3;
  }
`;

// Basic shimmer for standard mode
const basicShimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// Loading skeleton for content placeholders
const SkeletonContainer = styled.div<{ $galleryMode?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $galleryMode }) => 
    $galleryMode ? gallerySpacing.xs({ theme }) : theme.spacing.md};
`;

const SkeletonLine = styled.div<{ 
  $width?: string; 
  $height?: string; 
  $galleryMode?: boolean;
}>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  background: ${({ theme, $galleryMode }) => 
    $galleryMode 
      ? `linear-gradient(
          90deg,
          ${galleryColors.neutralLightest({ theme })} 25%,
          ${galleryColors.neutralLight({ theme })} 50%,
          ${galleryColors.neutralLightest({ theme })} 75%
        )`
      : `linear-gradient(
          90deg,
          ${theme.colors.gray.light} 25%,
          ${theme.colors.gray.medium} 50%,
          ${theme.colors.gray.light} 75%
        )`};
  background-size: 200% 100%;
  animation: ${({ $galleryMode }) => $galleryMode ? galleryShimmer : basicShimmer} 
    ${({ $galleryMode }) => $galleryMode ? '3s' : '2s'} ease-in-out infinite;
  border-radius: ${({ theme, $galleryMode }) => 
    $galleryMode ? galleryBorderRadius.subtle({ theme }) : theme.borderRadius.sm};
`;

const SkeletonCard = styled.div<{ $galleryMode?: boolean }>`
  padding: ${({ theme, $galleryMode }) => 
    $galleryMode ? gallerySpacing.artworkComfortable({ theme }) : theme.spacing.lg};
  border-radius: ${({ theme, $galleryMode }) => 
    $galleryMode ? galleryBorderRadius.comfortable({ theme }) : theme.borderRadius.lg};
  background-color: ${({ theme, $galleryMode }) => 
    $galleryMode ? galleryColors.white({ theme }) : theme.colors.white};
  box-shadow: ${({ theme, $galleryMode }) => 
    $galleryMode ? galleryShadows.artworkSubtle({ theme }) : theme.shadows.sm};
  
  ${({ $galleryMode }) => $galleryMode && `
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.05) 50%,
        rgba(0, 0, 0, 0.02) 100%
      );
      pointer-events: none;
      border-radius: inherit;
    }
  `}
`;

export interface LoadingSkeletonProps {
  lines?: number;
  lineWidths?: string[];
  height?: string;
  card?: boolean;
  galleryMode?: boolean;
  className?: string;
}

export function LoadingSkeleton({
  lines = 3,
  lineWidths = ['100%', '80%', '60%'],
  height = '16px',
  card = false,
  galleryMode = false,
  className,
}: LoadingSkeletonProps) {
  // Adjust defaults for gallery mode
  const adjustedHeight = galleryMode && height === '16px' ? '14px' : height;
  const adjustedLineWidths = galleryMode && lineWidths.length === 3 && lineWidths[0] === '100%' 
    ? ['90%', '70%', '85%'] : lineWidths;

  const content = (
    <SkeletonContainer className={className} $galleryMode={galleryMode}>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonLine
          key={index}
          $width={adjustedLineWidths[index] || adjustedLineWidths[adjustedLineWidths.length - 1]}
          $height={adjustedHeight}
          $galleryMode={galleryMode}
        />
      ))}
    </SkeletonContainer>
  );

  if (card) {
    return <SkeletonCard $galleryMode={galleryMode}>{content}</SkeletonCard>;
  }

  return content;
}

// Loading states for common UI patterns
export function LoadingButton({
  loading,
  children,
  ...props
}: {
  loading: boolean;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...props} disabled={loading || props.disabled}>
      {loading ? (
        <LoadingSpinner size="sm" color="white" text="Loading..." />
      ) : (
        children
      )}
    </button>
  );
}

// Full page loading state
const FullPageContainer = styled.div<{ $galleryMode?: boolean }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, $galleryMode }) => 
    $galleryMode ? galleryColors.white({ theme }) : theme.colors.background};
`;

export function FullPageLoading({ 
  text = 'Loading...', 
  galleryMode = false 
}: { 
  text?: string; 
  galleryMode?: boolean;
}) {
  return (
    <FullPageContainer $galleryMode={galleryMode}>
      <LoadingSpinner 
        size="lg" 
        text={text} 
        color={galleryMode ? 'accent' : 'primary'}
      />
    </FullPageContainer>
  );
}

// Gallery-specific artwork skeleton
const GalleryArtworkSkeleton = styled.div<{ $aspectRatio: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  background-color: ${galleryColors.white};
  border-radius: ${galleryBorderRadius.comfortable};
  box-shadow: ${galleryShadows.artworkSubtle};
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(0, 0, 0, 0.02) 100%
    );
    pointer-events: none;
    border-radius: inherit;
  }
`;

const GalleryImagePlaceholder = styled.div`
  position: absolute;
  top: ${gallerySpacing.artworkTight};
  left: ${gallerySpacing.artworkTight};
  right: ${gallerySpacing.artworkTight};
  bottom: 40%;
  background: linear-gradient(
    90deg,
    ${galleryColors.neutralLightest} 25%,
    ${galleryColors.neutralLight} 50%,
    ${galleryColors.neutralLightest} 75%
  );
  background-size: 200% 100%;
  animation: ${galleryShimmer} 3s ease-in-out infinite;
  border-radius: ${galleryBorderRadius.subtle};
  border: 1px solid ${galleryColors.neutralLight};
`;

const GalleryContentPlaceholder = styled.div`
  position: absolute;
  bottom: ${gallerySpacing.artworkComfortable};
  left: ${gallerySpacing.artworkComfortable};
  right: ${gallerySpacing.artworkComfortable};
  display: flex;
  flex-direction: column;
  gap: ${gallerySpacing.tiny};
`;

const GallerySkeletonLine = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 14px;
  background: linear-gradient(
    90deg,
    ${galleryColors.neutralLightest} 25%,
    ${galleryColors.neutralLight} 50%,
    ${galleryColors.neutralLightest} 75%
  );
  background-size: 200% 100%;
  animation: ${galleryShimmer} 3s ease-in-out infinite;
  border-radius: ${galleryBorderRadius.subtle};
`;

export function GalleryArtworkCardSkeleton({ 
  aspectRatio = 1 
}: { 
  aspectRatio?: number;
}) {
  return (
    <GalleryArtworkSkeleton $aspectRatio={aspectRatio}>
      <GalleryImagePlaceholder />
      <GalleryContentPlaceholder>
        <GallerySkeletonLine $width="75%" />
        <GallerySkeletonLine $width="55%" />
        <div style={{ display: 'flex', gap: '0.125rem' }}>
          <GallerySkeletonLine $width="20%" />
          <GallerySkeletonLine $width="25%" />
          <GallerySkeletonLine $width="18%" />
        </div>
      </GalleryContentPlaceholder>
    </GalleryArtworkSkeleton>
  );
}
